import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AnimeCardsGrid from '../components/AnimeCardsGrid';
import './AnimeCategoryPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const CATEGORY_CONFIG = {
  'top-rated': {
    title: 'Top Rated Anime',
    description: 'The highest rated anime based on user scores and reviews. These titles represent the very best that anime has to offer.',
    endpoint: '/api/anime/sections/top-rated',
    icon: 'fa-solid fa-star',
    bgColor: 'linear-gradient(135deg, #6c5ce7, #a29bfe)',
    defaultSort: 'rank',
    sortOptions: [
      { value: 'rank', label: 'Rank (Best to Worst)' },
      { value: 'score', label: 'Score (Highest to Lowest)' },
      { value: 'title', label: 'Title (A-Z)' },
      { value: 'popularity', label: 'Popularity' }
    ]
  },
  'best-of-year': {
    title: 'Best Anime of the Year',
    description: 'The top anime releases from this year that have captured audiences with their exceptional quality and storytelling.',
    endpoint: '/api/anime/sections/best-of-year',
    icon: 'fa-solid fa-trophy',
    bgColor: 'linear-gradient(135deg, #f39c12, #f1c40f)',
    defaultSort: 'score',
    sortOptions: [
      { value: 'score', label: 'Score (Highest to Lowest)' },
      { value: 'rank', label: 'Rank (Best to Worst)' },
      { value: 'title', label: 'Title (A-Z)' },
      { value: 'popularity', label: 'Popularity' }
    ]
  },
  'currently-airing': {
    title: 'Currently Airing Anime',
    description: 'Anime series that are broadcasting new episodes right now. Stay up-to-date with the freshest content this season.',
    endpoint: '/api/anime/sections/currently-airing',
    icon: 'fa-solid fa-calendar-check',
    bgColor: 'linear-gradient(135deg, #2ecc71, #27ae60)',
    defaultSort: 'popularity',
    sortOptions: [
      { value: 'popularity', label: 'Popularity (Most to Least)' },
      { value: 'score', label: 'Score (Highest to Lowest)' },
      { value: 'aired.from', label: 'Release Date (Newest First)' },
      { value: 'title', label: 'Title (A-Z)' }
    ]
  },
  'upcoming': {
    title: 'Upcoming Anime',
    description: 'Highly anticipated anime that are scheduled for release soon. Get a preview of what\'s next in the world of anime.',
    endpoint: '/api/anime/sections/upcoming',
    icon: 'fa-solid fa-clock',
    bgColor: 'linear-gradient(135deg, #e74c3c, #c0392b)',
    defaultSort: 'popularity',
    sortOptions: [
      { value: 'popularity', label: 'Popularity (Most to Least)' },
      { value: 'aired.from', label: 'Release Date (Soonest First)' },
      { value: 'title', label: 'Title (A-Z)' }
    ]
  },
  'movies': {
    title: 'Anime Movies',
    description: 'Feature-length anime films that deliver compelling stories in a single spectacular package.',
    endpoint: '/api/anime/sections/movies',
    icon: 'fa-solid fa-film',
    bgColor: 'linear-gradient(135deg, #3498db, #2980b9)',
    defaultSort: 'score',
    sortOptions: [
      { value: 'score', label: 'Score (Highest to Lowest)' },
      { value: 'title', label: 'Title (A-Z)' },
      { value: 'popularity', label: 'Popularity' },
      { value: 'aired.from', label: 'Release Date (Newest First)' }
    ]
  }
};

const AnimeCategoryPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({
    animes: [],
    loading: true,
    error: null,
    page: 1,
    itemsPerPage: 24,
    totalPages: 1,
    sortOrder: 'rank',
    cardLayout: window.innerWidth <= 768 ? "vertical" : "horizontal"
  });

  const categoryConfig = CATEGORY_CONFIG[category];

  // Resize handler for layout switching
  useEffect(() => {
    const handleResize = () => {
      setData(prev => ({
        ...prev,
        cardLayout: window.innerWidth <= 768 ? "vertical" : "horizontal"
      }));
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch anime data
  useEffect(() => {
    if (!categoryConfig) {
      setData(prev => ({ ...prev, loading: false }));
      navigate('/not-found');
      return;
    }

    const fetchData = async () => {
      setData(prev => ({ ...prev, loading: true, error: null }));
      
      try {
        const url = new URL(`${API_BASE_URL}${categoryConfig.endpoint}`);
        url.searchParams.append('page', data.page);
        url.searchParams.append('limit', Math.min(100, data.itemsPerPage));
        url.searchParams.append('sort', data.sortOrder);

        const response = await axios.get(url.toString());
        setData(prev => ({
          ...prev,
          animes: response.data.data || [],
          totalPages: response.data.pagination?.pages || 1,
          loading: false
        }));
      } catch (error) {
        setData(prev => ({
          ...prev,
          error: "Failed to load anime. Please try again later.",
          loading: false
        }));
        console.error('Error fetching anime:', error);
      }
    };

    fetchData();
  }, [category, data.page, data.itemsPerPage, data.sortOrder]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= data.totalPages) {
      setData(prev => ({ ...prev, page: newPage }));
      window.scrollTo(0, 0);
    }
  };

  const handleSortChange = (e) => {
    setData(prev => ({
      ...prev,
      sortOrder: e.target.value,
      page: 1
    }));
  };

  const handleItemsPerPageChange = (e) => {
    setData(prev => ({
      ...prev,
      itemsPerPage: Math.min(100, Number(e.target.value)),
      page: 1
    }));
  };

  // Category Header Component
  const CategoryHeader = () => (
    <div 
      className="category-header" 
      style={{ background: categoryConfig.bgColor }}
    >
      <div className="category-header-content">
        <div className="category-icon">
          <i className={categoryConfig.icon}></i>
        </div>
        <div className="category-info">
          <h1>{categoryConfig.title}</h1>
          <p>{categoryConfig.description}</p>
        </div>
      </div>
    </div>
  );

  if (!categoryConfig && data.loading) return <div className="loading-spinner"></div>;
  if (!categoryConfig) return null;

  return (
    <div className="category-page">
      <CategoryHeader />

      <div className="category-content">
        <div className="category-controls">
          <div className="results-info">
            {data.animes.length > 0 ? (
              <div className="result-stats">
                <span className="result-count">{data.animes.length} anime</span>
                {data.totalPages > 1 && (
                  <span className="page-info">Page {data.page} of {data.totalPages}</span>
                )}
              </div>
            ) : (
              <span>No results found</span>
            )}
          </div>
          
          <div className="sort-controls">
            <label className="sort-select">Sort by:</label>
            <select 
              className="sort-select" 
              value={data.sortOrder}
              onChange={handleSortChange}
            >
              {categoryConfig.sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            
            <label className="limit-select">Show:</label>
            <select
              className="limit-select"
              value={data.itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value={12}>12 per page</option>
              <option value={24}>24 per page</option>
              <option value={48}>48 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>

        {data.loading ? (
          <div className="loading-spinner"></div>
        ) : data.error ? (
          <div className="error-message">
            {data.error}
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        ) : (
          <>
            <AnimeCardsGrid 
              animes={data.animes} 
              layout={data.cardLayout} 
            />

            {data.totalPages > 1 && (
              <div className="pagination">
                <button
                  className='page-btn'
                  onClick={() => handlePageChange(data.page - 1)}
                  disabled={data.page === 1}
                >
                  Previous
                </button>
                
                <div className="page-numbers">
                  {[...Array(Math.min(data.totalPages, 7))].map((_, i) => {
                    let pageNum;
                    
                    if (data.totalPages <= 7) {
                      pageNum = i + 1;
                    } 
                    else if (data.page <= 3) {
                      if (i < 5) pageNum = i + 1;
                      else if (i === 5) return <span key="ellipsis1">...</span>;
                      else pageNum = data.totalPages;
                    } 
                    else if (data.page >= data.totalPages - 2) {
                      if (i === 0) pageNum = 1;
                      else if (i === 1) return <span key="ellipsis2">...</span>;
                      else pageNum = data.totalPages - (6 - i);
                    } 
                    else {
                      if (i === 0) pageNum = 1;
                      else if (i === 1) return <span key="ellipsis3">...</span>;
                      else if (i === 5) return <span key="ellipsis4">...</span>;
                      else if (i === 6) pageNum = data.totalPages;
                      else pageNum = data.page + (i - 3);
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        className={pageNum === data.page ? 'active' : ''}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  className='page-btn'
                  onClick={() => handlePageChange(data.page + 1)}
                  disabled={data.page === data.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AnimeCategoryPage;